import * as d3 from 'd3';

import BenchlingConfig from 'BenchlingConfig';
import {Part} from 'biojs/Part';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {
  PART_ANNOTATION_ARCHIVED_COLOR,
  PART_ANNOTATION_ARCHIVED_OPACITY,
  PART_ANNOTATION_COLOR,
  PART_ANNOTATION_OUT_OF_SYNC_COLOR,
  SINGLE_ANNOTATION_RADIUS,
} from 'molbio/maps/BiointelligentLinksConstants';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import LabeledSegmentsRingView from 'molbio/maps/circular/LabeledSegmentsRingView';
import {NEAR_BLACK, WHITE} from 'ui-library/Colors';

/**
 * View for drawing parts on the plasmid map as annotations when overlapping parts is enabled in addition
 * to rendering part overlays on the axis (See PartsView)
 */
class CircularSequencePartAnnotationsView extends LabeledSegmentsRingView<RingLayout, Part> {
  singularClassName = 'part-annotation';

  static initClass(): void {
    this.prototype.className = 'part-annotations';
    this.prototype.singularClassName = 'part-annotation';
    this.prototype.tagName = 'g';

    // Need to set contextMenuData to null to default to showing the parts context menu.
    // Should revisit this behavior to match other features.
    // @ts-expect-error
    this.prototype.contextMenuData = null;
  }

  contextMenuOptions(d: Part): MenuChoicesOptions {
    return {tabstate: this.tabstate};
  }

  arc(d: Part): d3.Arc<unknown, d3.DefaultArcObject> {
    const arc = d3.arc();
    if (
      BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS &&
      BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS
    ) {
      arc.cornerRadius(SINGLE_ANNOTATION_RADIUS);
    }
    return arc;
  }

  fill(d: Part): string {
    const outOfSync = !d.isInSync(this.model.polymer()._sequence());
    const isArchived = d.sequence().isArchived();
    if (outOfSync) {
      return PART_ANNOTATION_OUT_OF_SYNC_COLOR;
    } else if (isArchived) {
      return PART_ANNOTATION_ARCHIVED_COLOR;
    } else {
      return PART_ANNOTATION_COLOR;
    }
  }

  fontColor(d: Part): string {
    return d.sequence().isArchived() ? NEAR_BLACK : WHITE;
  }

  opacity(d: Part): number {
    return d.sequence().isArchived() ? PART_ANNOTATION_ARCHIVED_OPACITY : super.opacity(d);
  }

  data(): Array<Part> {
    // Only show part annotations if overlapping parts is enabled, otherwise PartsView already shows labels.
    if (BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      return this.model
        .get('sequence')
        .parts()
        .getSequencePartsBetween(this.model.get('leftBP'), this.model.get('rightBP'));
    }
    return [];
  }

  clickAction(e: MouseEvent, d: Part): void {
    d.click(this.model.polymer(), e.shiftKey);
  }

  dblClickAction(e: MouseEvent, d: Part): void {
    d.dblClick(this.model.polymer(), e.shiftKey);
  }

  registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:bases', () => {
      this.invalidateData();
    });
  }

  setConstraints(outerRadius: number): void {
    this.setOuterRadius(outerRadius);
  }

  redrawPlasmid(): boolean {
    return true;
  }

  storeBounds(rings?: number | null): void {
    if (rings == null) {
      ({rings} = this);
    }
    this.rings = rings;
    const innerRadius = this.getOuterRadius() - this.layout.get('ringHeight') * this.rings!;
    this.layout.set('innerRadius', innerRadius);
    this.updateRadius();
  }

  zeroBounds(): void {
    this.layout.set('innerRadius', this.layout.get('outerRadius'));
    this.updateRadius();
  }

  layerBounds(layer: number): {innerRadius: number; outerRadius: number} {
    return {
      innerRadius: this.radiusIn(layer + 1),
      outerRadius: this.radiusIn(layer) - this.layout.vMargin(),
    };
  }

  baseLine(layer: number): number {
    return this.radiusIn(layer + 1);
  }
}
CircularSequencePartAnnotationsView.initClass();

export default CircularSequencePartAnnotationsView;
